import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Printer, ArrowLeft } from "lucide-react";

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-6 border-b border-neutral-200 py-2.5 text-sm">
      <span className="text-neutral-500 uppercase tracking-wide text-xs">{label}</span>
      <span className="text-neutral-900 font-medium text-right">{value || "—"}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400 mb-3">{title}</h2>
      <div>{children}</div>
    </div>
  );
}

export default function TripSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/trip-sheet/${id}`)
      .then(({ data }) => setData(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-neutral-900" /></div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4" data-testid="trip-sheet-error">
        <p className="text-neutral-700">Trip sheet not found.</p>
        <Button variant="outline" onClick={() => navigate("/admin")}>Back to dashboard</Button>
      </div>
    );
  }

  const r = data.reservation || {};
  const v = data.vehicle;
  const d = data.driver;
  const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || "—"; } };

  return (
    <div className="min-h-screen bg-neutral-100 py-10 print:bg-white print:py-0" data-testid="trip-sheet-page">
      {/* Toolbar (hidden when printing) */}
      <div className="max-w-3xl mx-auto px-6 mb-6 flex items-center justify-between print:hidden">
        <Button variant="ghost" onClick={() => navigate("/admin")} data-testid="trip-sheet-back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <Button onClick={() => window.print()} data-testid="trip-sheet-print-btn" className="bg-neutral-900 text-white hover:bg-neutral-800 rounded-full px-6">
          <Printer className="h-4 w-4 mr-2" /> Print / Save PDF
        </Button>
      </div>

      {/* Sheet */}
      <div className="max-w-3xl mx-auto bg-white text-neutral-900 shadow-sm print:shadow-none px-10 py-10 print:px-0" data-testid="trip-sheet-content">
        <div className="flex items-start justify-between border-b-2 border-neutral-900 pb-5 mb-8">
          <div>
            <h1 className="font-serif text-2xl">Autobus Limo Mont Royal</h1>
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-1">Trip Sheet</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-neutral-400">Reservation</p>
            <p className="font-mono text-lg font-semibold" data-testid="trip-sheet-ref">{r.reservation_number || r.id}</p>
            <p className="text-xs text-neutral-500 mt-1 capitalize">{(r.status || "").replace(/_/g, " ")}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-x-12">
          <Section title="Trip">
            <Row label="Service" value={r.service_label} />
            <Row label="Trip type" value={(r.trip_type || "").replace(/_/g, " ")} />
            <Row label="Date" value={r.date} />
            <Row label="Time" value={r.time} />
            <Row label="Passengers" value={r.passengers} />
            <Row label="Luggage" value={r.luggage} />
          </Section>

          <Section title="Route">
            <Row label="Pickup" value={r.pickup_location} />
            <Row label="Drop-off" value={r.dropoff_location} />
            <Row label="Flight #" value={r.flight_number} />
            <Row label="Start" value={fmtDateTime(r.start_dt)} />
            <Row label="End" value={fmtDateTime(r.end_dt)} />
          </Section>

          <Section title="Client">
            <Row label="Name" value={r.name} />
            <Row label="Email" value={r.email} />
            <Row label="Phone" value={r.phone} />
            <Row label="Occasion" value={r.occasion} />
          </Section>

          <Section title="Assignment">
            <Row label="Vehicle" value={v ? `${v.name}${v.unit_number ? ` · Unit ${v.unit_number}` : ""}` : "Unassigned"} />
            <Row label="Vehicle class" value={v?.vehicle_class} />
            <Row label="Driver" value={d?.name || "Unassigned"} />
            <Row label="Driver phone" value={d?.phone} />
          </Section>
        </div>

        {r.special_requests && (
          <Section title="Special requests">
            <p className="text-sm text-neutral-800 leading-relaxed">{r.special_requests}</p>
          </Section>
        )}

        <div className="grid grid-cols-2 gap-12 mt-12 pt-8 border-t border-neutral-300">
          <div>
            <div className="border-b border-neutral-400 h-10" />
            <p className="text-xs text-neutral-500 mt-2">Driver signature</p>
          </div>
          <div>
            <div className="border-b border-neutral-400 h-10" />
            <p className="text-xs text-neutral-500 mt-2">Client signature</p>
          </div>
        </div>
      </div>
    </div>
  );
}
